import { Dispatch, SetStateAction } from 'react';
import { type BubbleComponentType } from '../../bubble/bubble';
import { ComponentType } from '@/lib/types';

/**
 * Creates a new chat bubble component with the given type and properties.
 * If the time is not provided, it defaults to the current time in 'HH:MM' format.
 *
 * @param {BubbleComponentType} bubble - The bubble component details.
 * @param {string} bubble.type - The type of the bubble, must be 'Bubble'.
 * @param {Object} bubble.props - The properties of the bubble.
 * @param {string} [bubble.props.time] - The time the message was created.
 * @param {boolean} bubble.props.user - Indicates if the bubble is from the user.
 * @param {boolean} bubble.props.loading - Indicates if the bubble is in loading state.
 * @param {string} bubble.props.message - The message content of the bubble.
 * @param {AnswerStatus} bubble.props.status - The status of the bubble (e.g., 'sent', 'delivered', 'read').
 * @param {boolean} [bubble.props.humanHandover] - Optional flag to indicate human handover.
 * @returns {BubbleComponentType} - The created bubble component.
 */
export const createChatBubble = ({
	type,
	props,
	firstItem,
}: BubbleComponentType): BubbleComponentType => {
	const { time, ...rest } = props;
	const conversation: BubbleComponentType = {
		type: type,
		props: {
			time: time
				? time
				: new Date().toLocaleTimeString([], {
						hour: '2-digit',
						minute: '2-digit',
					}),
			...rest,
		},
		firstItem: firstItem ?? false,
	};
	return conversation;
};

/**
 * Adds a new bubble to the conversation as a loader
 *
 * This function adds a new bubble loader to the conversation state immediately, and
 * will be removed as soon as mutation.isLoading || refreshMutation.isLoading state is false
 * @param {Dispatch<SetStateAction<BubbleComponentType[]>>} setConversation - The new bubble loader component to add to the conversation.
 * @returns {void}
 */
export const createChatBubbleLoader = ({
	setConversation,
}: {
	setConversation: Dispatch<SetStateAction<BubbleComponentType[]>>;
}): void => {
	const bubbleObjLoading: BubbleComponentType = createChatBubble({
		type: ComponentType.Bubble,
		props: {
			user: false,
			loading: true,
			message: '',
			status: '200',
		},
	});
	setConversation((prev) => [...prev, bubbleObjLoading]);
};

/**
 * Adds a new bubble to the conversation and updates the loading state after a timeout.
 *
 * This function adds a new bubble to the conversation state immediately,
 * it will trigger a loading state to appear through mutation.isLoading || refreshMutation.isLoading.
 * If the new bubble's status is '200' and it has human handover enabled,
 * it triggers the human handover process.
 *
 * @param {BubbleComponentType} newBubble - The new bubble component to add to the conversation.
 * @param {Dispatch<SetStateAction<BubbleComponentType[]>>} setConversation - The state setter function for updating the conversation.
 * @param {Dispatch<SetStateAction<boolean>>} setHumanHandover - The state setter function for updating the human handover flag.
 * @returns {void}
 */
export const updateConversation = (
	newBubble: BubbleComponentType,
	setConversation: Dispatch<SetStateAction<BubbleComponentType[]>>,
	setHumanHandover: Dispatch<SetStateAction<boolean>>
): void => {
	setConversation((prev) => [...prev, newBubble]);

	setConversation((prev) =>
		prev.map((component, index) =>
			index === prev.length - 1
				? {
						...component,
						props: {
							...component.props,
							loading: false,
						},
					}
				: component
		)
	);

	if (newBubble.props.humanHandover) {
		setHumanHandover(true);
	}
};

/**
 * Handles setting or removing the tabindex attribute on flow content elements
 * within the chat-window-components element based on the open state.
 *
 * @param {boolean} open - A boolean indicating whether to set tabindex to -1 (true) or 0 (false).
 */
export const tabIndexHandler = (open: boolean) => {
	const body = document.getElementById('chat-window-components');
	if (body) {
		const flowContentElements = body.querySelectorAll(
			'a, button, input, select, textarea, [tabindex]'
		);
		// Iterate over each element and remove the tabindex attribute
		flowContentElements.forEach((element) => {
			if (open) {
				element.setAttribute('tabindex', '-1');
				element.setAttribute('aria-hidden', 'true');
				element.setAttribute('disabled', 'true');
			} else {
				element.setAttribute('tabindex', '0');
				element.removeAttribute('aria-hidden');
				element.removeAttribute('disabled');
			}
		});
	}
};

/**
 * Locks tabbing (focus trap) inside a given HTML container.
 *
 * This utility ensures that keyboard focus using the Tab key
 * is trapped within the specified DOM node, preventing the user
 * from tabbing outside of it — useful for modals, dialogs, or
 * any isolated UI section like a chat window.
 *
 * @param {React.MutableRefObject<HTMLDivElement | null>} ref - A ref to the container element to trap focus within.
 * @returns {() => void} A cleanup function that removes the keydown listener.
 *
 * @example
 * const ref = useRef<HTMLDivElement>(null);
 * useEffect(() => {
 *   const cleanup = lockTabbing(ref);
 *   return cleanup;
 * }, []);
 */
export const lockTabbing = (
	ref: React.MutableRefObject<HTMLDivElement | null>
): (() => void) => {
	if (!ref.current) return () => {};
	const focusableSelectors =
		'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex="0"], [contenteditable]';
	const focusableElements =
		ref.current?.querySelectorAll<HTMLElement>(focusableSelectors);

	if (!focusableElements || focusableElements.length === 0) return () => {};

	const firstElement = focusableElements[0];
	const lastElement = focusableElements[focusableElements.length - 1];

	const handleKeyDown = (e: KeyboardEvent) => {
		if (e.key !== 'Tab') return;

		if (e.shiftKey) {
			if (document.activeElement === firstElement) {
				e.preventDefault();
				lastElement?.focus();
			}
		} else {
			if (document.activeElement === lastElement) {
				e.preventDefault();
				firstElement?.focus();
			}
		}
	};

	document.addEventListener('keydown', handleKeyDown);

	return () => {
		document.removeEventListener('keydown', handleKeyDown);
	};
};
